// Where a dragged hero slider settles when the pointer is let go.
//
// The hero is a transform track of full-width slides, so a release either
// commits to a neighbouring slide or springs back to the one it started on.
// Pure, so the decision is testable without a DOM; the component owns the
// pointer listeners and the transform.

// How far a release has to have travelled before it changes slide.
export const HERO_DRAG_COMMIT_PX = 50;

// Slides are a ring: past the last one is the first. Matches the component's
// own `goTo`, so a drag and a dot click cannot disagree about where they land.
const wrap = (index, count) => ((index % count) + count) % count;

// A campaign expiring mid-gesture can shorten the list under the drag, so the
// slide it started from is not necessarily one that still exists.
const slideFrom = (index, count) =>
  Number.isFinite(index) ? wrap(Math.trunc(index), count) : 0;

/**
 * The slide to settle on after a drag of `dx` pixels released on `index`.
 *
 * Dragging left is a negative `dx` and moves forward, because the track
 * follows the hand. A hero with one slide, or none, has nowhere to go.
 */
export const slideAfterDrag = ({
  index,
  dx,
  count,
  threshold = HERO_DRAG_COMMIT_PX,
} = {}) => {
  if (!Number.isFinite(count) || count <= 1) return 0;

  const from = slideFrom(index, count);

  // Checked before the comparisons below, where a -Infinity would otherwise
  // read as a huge leftward drag and a NaN would settle the hero on no slide
  // at all.
  if (!Number.isFinite(dx)) return from;

  const commitAt = Number.isFinite(threshold)
    ? Math.abs(threshold)
    : HERO_DRAG_COMMIT_PX;

  if (dx <= -commitAt) return wrap(from + 1, count);
  if (dx >= commitAt) return wrap(from - 1, count);

  return from;
};
